// ============================================================
// AFFLICT AND CURSE (v8.368.4) - a hit lays a condition on THEM, or on
// the one who hit.
//
// `afflict=<code>` is the target-side twin of a weapon affliction: any
// hit on the bearer applies the code to the bearer (Soaked in promethium
// - any Energy hit sets them ablaze, `afflict:E=Fi`; Glass-boned - any hit
// stuns). `curse=<code>` goes the other way: whoever hits them wears it
// (Shocking - Stunned; Venomous - Blood Loss). Both values are TIMELESS
// codes exactly as the picker stores them; the stacking policy and the
// immunity map are the caller's, as for every applied condition.
//
// Pure. Zero React dependency; a leaf beside exposureHazards.
// ============================================================
import {
  activeExposures,
  exposureLabel,
  type Axis,
  type Exposure,
  type ExposureBearer,
  type ExposureReader,
} from './exposures';

export interface LaidCondition {
  /** The timeless condition code to apply. */
  code: string;
  /** The exposure it came from, for the line in the log. */
  label: string;
}

/** What a landed hit lays on the BEARER: `afflict`, and `afflict:<type>` when the hit is of that type. */
export function afflictionsFromExposures(
  bearer: ExposureBearer,
  reader: ExposureReader,
  damageType: string | null | undefined,
): LaidCondition[] {
  return activeExposures(bearer, reader).flatMap((ex) => laidBy(ex, damageType));
}

const DAMAGE_TYPES = new Set(['E', 'I', 'R', 'X']);

/** One exposure's afflictions for this hit: the plain one, then the one typed to the hit's damage. */
function laidBy(ex: Exposure, damageType: string | null | undefined): LaidCondition[] {
  const label = exposureLabel(ex);
  const out: LaidCondition[] = [];
  if (typeof ex.axes.afflict === 'string') out.push({ code: ex.axes.afflict, label });
  const typed = damageType && DAMAGE_TYPES.has(damageType) ? ex.axes[`afflict:${damageType}` as Axis] : undefined;
  if (typeof typed === 'string') out.push({ code: typed, label });
  return out;
}

/** What a landed hit lays on the ATTACKER: `curse`. */
export function cursesFromExposures(bearer: ExposureBearer, reader: ExposureReader): LaidCondition[] {
  const out: LaidCondition[] = [];
  for (const ex of activeExposures(bearer, reader)) {
    if (typeof ex.axes.curse === 'string') out.push({ code: ex.axes.curse, label: exposureLabel(ex) });
  }
  return out;
}
